import type { ErrorCode } from './contract.gen'

/** Codes that are not produced by the server but by the client itself. */
export type ClientCode = 'network' | 'unknown'

/** The only error type of the data layer. */
export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode | ClientCode
  readonly details: Record<string, unknown>

  constructor(status: number, code: ErrorCode | ClientCode, message: string, details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** A moment from details (ready_at, arrives_at, until…) as a Date. */
  at(field: string): Date | null {
    const v = this.details[field]
    if (typeof v !== 'string') return null
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError

/** Player-facing text for every server code. The compiler checks that each code has one. */
export const ERROR_TEXT: Record<ErrorCode | ClientCode, string> = {
  premium_spending_locked: 'Траты кристаллов недоступны до погашения корректировки возврата',
  payment_unverified: 'Платёж ещё не подтверждён или заказ недоступен',
  ad_session_invalid: 'Просмотр уже завершён, истёк или недоступен',
  mentor_not_found: 'Наставник не найден',
  mentor_not_here: 'Сначала придите к наставнику',
  mentor_locked: 'Сначала выполните условие наставника',
  item_not_sellable: 'Эту вещь нельзя продать: она надета или не принимается торговцем',
  price_changed: 'Цена изменилась. Обновите лавку и подтвердите новую цену',
  network: 'Связь потеряна. Попробуйте ещё раз',
  unknown: 'Что-то пошло не так',
  account_banned: 'Аккаунт заблокирован',
  action_active: 'Герой занят делом',
  action_unavailable: 'Здесь этого не сделать',
  already_claimed: 'Награда уже получена',
  already_full: 'Восстанавливать нечего: силы и так полны',
  already_in_clan: 'Вы уже в клане',
  already_reported: 'Жалоба уже отправлена',
  attribute_cap: 'Свойство упирается в предел',
  attribute_required: 'Не хватает свойства',
  battle_finished: 'Бой уже закончен',
  battle_not_found: 'Бой не найден',
  board_limit: 'Взято слишком много поручений',
  board_locked: 'Доска откроется позже',
  channel_forbidden: 'В этот канал писать нельзя',
  chapter_locked: 'Эта глава повести ещё не открыта',
  chapter_not_found: 'Такой главы нет',
  chat_muted: 'Вам запрещено писать в чат',
  choice_already_made: 'Выбор уже сделан',
  choice_locked: 'Ход в этом раунде уже сделан',
  clan_full: 'В клане нет мест',
  clan_name_taken: 'Название клана занято',
  clan_not_found: 'Клана нет',
  content_invalid: 'Контент не проходит проверку',
  daily_limit_reached: 'Рейтинговые бои на сегодня закончились',
  description_too_long: 'Описание слишком длинное',
  document_not_found: 'Документ не найден',
  encounter_closed: 'Встреча уже закрыта',
  encounter_not_found: 'Встречи нет',
  encounter_pending: 'Сначала решите, что делать со встречей',
  faith_cooldown: 'Сменить покровителя можно позже',
  forbidden_role: 'Нет прав на это действие',
  god_already_chosen: 'Этот бог уже ваш покровитель',
  god_not_found: 'Такого бога нет',
  goods_not_found: 'Такого товара или рецепта здесь нет',
  hero_exists: 'Герой уже создан',
  hero_not_found: 'Героя ещё нет',
  impassable: 'Туда не пройти',
  in_combat: 'Во время боя нельзя',
  in_place: 'Сначала выйдите из места',
  injured: 'Тяжёлая травма: пока нельзя ни идти, ни драться',
  injury_eased: 'Эту травму уже облегчали',
  insufficient_focus: 'Не хватает напора',
  insufficient_funds: 'Не хватает серебра',
  insufficient_items: 'Не хватает предметов',
  insufficient_mana: 'Не хватает маны',
  internal: 'Не удалось выполнить действие. Попробуйте ещё раз',
  invalid_bundle: 'Неверный состав выдачи',
  invalid_choice: 'Такого ответа нет',
  invalid_clan_name: 'Название: буквы, цифры, пробел и дефис',
  invalid_credentials: 'Неверный логин или пароль',
  invalid_level: 'Уровень или опыт вне пределов',
  invalid_position: 'Такой клетки или места нет',
  invalid_quest_step: 'Такого шага нет',
  invalid_recipient: 'Некому отправить',
  invalid_request: 'Неверный запрос',
  invalid_signature: 'Подпись площадки не принята',
  invalid_state: 'Сейчас так нельзя',
  invalid_target: 'Цель недоступна',
  level_required: 'Нужен уровень выше',
  login_taken: 'Логин уже занят',
  mastery_required: 'Не хватает мастерства школы',
  media_invalid: 'Файл повреждён',
  media_too_large: 'Файл слишком большой',
  media_type_unsupported: 'Такой тип файла не принимается',
  message_not_found: 'Сообщения нет',
  name_taken: 'Имя уже занято',
  not_adjacent: 'Идти можно только на соседнюю клетку',
  not_at_healer: 'Лечат только у лекаря',
  not_clan_member: 'Вы не в клане',
  not_clan_owner: 'Это может только владелец клана',
  not_current_step: 'Сейчас нужно другое',
  not_enough_points: 'Не хватает свободных очков',
  not_enough_skill_points: 'Нет свободных очков умений',
  not_enough_stamina: 'Не хватает выносливости',
  not_found: 'Не найдено',
  not_in_place: 'Вы не в месте',
  not_injured: 'Травмы нет',
  not_owned: 'Этого предмета у вас нет',
  not_participant: 'Вы не участник этого боя',
  not_ready: 'Ещё не выполнено',
  not_request_owner: 'Отменить может только создатель',
  not_usable: 'Это нельзя выпить или съесть',
  offer_not_found: 'Поручения нет',
  out_of_bracket: 'Соперник вне вашего коридора',
  own_request_open: 'У вас уже есть заявка',
  owner_must_transfer: 'Сначала передайте клан другому участнику',
  password_identity_required: 'Нужен аккаунт со входом по паролю',
  place_not_here: 'Такого места здесь нет',
  player_not_found: 'Игрока нет',
  provider_not_configured: 'Вход через эту площадку не настроен',
  quest_not_found: 'Задания нет',
  rate_limited: 'Не так быстро',
  reason_required: 'Укажите причину',
  recipient_not_found: 'Такого героя нет',
  release_base_outdated: 'С тех пор опубликован другой релиз',
  release_not_editable: 'Релиз не меняется',
  release_not_found: 'Релиз не найден',
  release_not_validated: 'Сначала проверьте релиз',
  request_closed: 'Заявка уже закрыта',
  request_not_found: 'Заявки нет',
  request_reused: 'Запрос уже использован',
  round_mismatch: 'Раунд уже сменился',
  session_revoked: 'Сессия завершена',
  shop_not_found: 'Такой лавки нет',
  shop_not_here: 'Эта лавка в другом месте',
  silenced: 'В забытьи — только удар или передышка',
  simulation_required: 'Нужна пройденная симуляция',
  simulation_running: 'Симуляция уже идёт',
  skill_locked: 'Ступень школы ещё закрыта',
  skill_needs_weapon: 'Умению нужно другое оружие',
  skill_not_in_set: 'Этого умения нет в боевом наборе',
  skill_on_cooldown: 'Умение ещё не готово',
  slot_mismatch: 'Предмет не подходит к ячейке',
  stunned: 'Оглушённый не применяет приёмов',
  text_empty: 'Пустое сообщение',
  text_too_long: 'Слишком длинный текст',
  too_weak: 'Герой слишком слаб для боя',
  traveling: 'Герой ещё в пути',
  unauthorized: 'Нужен вход',
  unknown_item: 'Такого предмета нет',
  unknown_mode: 'Такого режима нет',
  use_limit_reached: 'На сегодня хватит: отваров больше не выпить',
  version_conflict: 'Данные изменились, обновляем',
  wrong_choice: 'Не то',
  wrong_object: 'Сейчас нужно другое',
  wrong_place: 'Это делается в другом месте',
}

/** Text for an error shown to a person. */
export function errorText(e: unknown): string {
  // A malformed field is explained by the server's own words (which field and why).
  if (isApiError(e) && e.code === 'invalid_request' && e.message) return e.message
  if (isApiError(e)) return ERROR_TEXT[e.code] ?? e.message
  return ERROR_TEXT.unknown
}
